import React from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardContent } from '@/components/ui/card';
import { cn } from '@/lib/utils';
import { HardHat, Wallet, Receipt, AlertTriangle } from 'lucide-react';
import { formatUsd } from '@/lib/jobsData';

const Stat = ({ label, value, hint, Icon, tone = 'default', testId }) => {
  const toneStyles = {
    default: 'text-foreground',
    primary: 'text-[hsl(var(--primary))]',
    success: 'text-[hsl(var(--success))]',
    warning: 'text-[hsl(var(--warning))]',
    destructive: 'text-[hsl(var(--destructive))]',
  };
  const accentBar = {
    default: 'bg-border',
    primary: 'bg-[hsl(var(--primary))]/70',
    success: 'bg-[hsl(var(--success))]/70',
    warning: 'bg-[hsl(var(--warning))]/70',
    destructive: 'bg-[hsl(var(--destructive))]/70',
  };
  return (
    <Card
      className="relative overflow-hidden border-[hsl(var(--hairline))] bg-[hsl(var(--card))] hover:border-[hsl(var(--primary))]/30 transition-colors"
      data-testid={testId}
    >
      <span
        aria-hidden
        className={cn('metallic absolute left-0 top-0 h-full w-[2px]', accentBar[tone])}
      />
      <CardContent className="p-4 sm:p-5">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="text-[11px] leading-snug sm:text-sm lg:text-xs 2xl:text-sm font-bold uppercase tracking-[0.12em] break-words text-[hsl(var(--primary))]">
              {label}
            </div>
            <div
              className={cn('mt-1.5 text-lg sm:text-xl font-semibold tracking-tight tabular-nums truncate', toneStyles[tone])}
              data-testid={testId ? `${testId}-value` : undefined}
            >
              {value}
            </div>
            {hint ? (
              <div className="mt-1 text-xs text-muted-foreground truncate">{hint}</div>
            ) : null}
          </div>
          {Icon ? (
            <div
              className={cn(
                'hidden sm:flex shrink-0 size-9 lg:size-8 2xl:size-9 rounded-md border border-border bg-[hsl(var(--muted))] items-center justify-center',
                toneStyles[tone]
              )}
            >
              <Icon size={18} strokeWidth={1.75} />
            </div>
          ) : null}
        </div>
      </CardContent>
    </Card>
  );
};

export const JobsStatsBar = ({ stats }) => {
  const { t } = useTranslation();
  const { pipeline = 0, collected = 0, outstanding = 0, count = 0 } = stats || {};
  return (
    <div
      className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4"
      data-testid="jobs-stats"
    >
      <Stat
        label={t('jobs.stats.contract_total')}
        value={formatUsd(pipeline)}
        Icon={Wallet}
        tone="primary"
        hint={t('jobs.subtitle')}
        testId="jobs-stat-pipeline"
      />
      <Stat
        label={t('jobs.stats.collected')}
        value={formatUsd(collected)}
        Icon={Receipt}
        tone="success"
        hint={t('common.paid')}
        testId="jobs-stat-collected"
      />
      <Stat
        label={t('jobs.stats.outstanding')}
        value={formatUsd(outstanding)}
        Icon={AlertTriangle}
        tone={outstanding > 0 ? 'warning' : 'default'}
        hint={t('common.outstanding')}
        testId="jobs-stat-outstanding"
      />
      <Stat
        label={t('jobs.stats.active')}
        value={count}
        Icon={HardHat}
        tone="default"
        hint={t('common.showing')}
        testId="jobs-stat-count"
      />
    </div>
  );
};

export default JobsStatsBar;
